import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Stethoscope, ArrowRight, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function ProfessionalsDirectory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [profs, setProfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const isPatient = !!localStorage.getItem("patient_token");

  useEffect(() => {
    api.get("/public/professional-specializations").then((r) => setSpecialties(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (specialty) params.set("specialization", specialty);
      api.get(`/public/professionals?${params.toString()}`)
        .then((r) => setProfs(r.data || []))
        .catch(() => setProfs([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [q, specialty]);

  async function linkAndGo(prof) {
    if (isPatient) {
      try {
        const token = localStorage.getItem("patient_token");
        await api.post("/patient-portal/link-doctor", { doctor_slug: prof.slug },
          { headers: { Authorization: `Bearer ${token}` } });
        navigate("/mi-portal");
      } catch { navigate(`/reservation/${prof.slug}`); }
    } else {
      navigate(`/reservation/${prof.slug}`);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="professionals-directory">
      <header className="sticky top-0 z-40 bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-stone-200/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={isPatient ? "/mi-portal" : "/bienvenido"} className="flex items-baseline gap-2">
            <span className="font-heading italic text-2xl text-[#B07D4F]">A</span>
            <span className="font-heading text-2xl font-light">Ambra</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-400 hidden sm:inline">patient matter</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to={isPatient ? "/mi-portal" : "/portal"} data-testid="back-link"
              className="text-sm text-stone-600 hover:text-stone-900 flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> {t("directory.back")}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#B07D4F] mb-3">{t("directory.eyebrow")}</p>
        <h1 className="font-heading text-4xl md:text-5xl font-light mb-3">{t("directory.title")}</h1>
        <p className="text-stone-600 mb-10 max-w-2xl">{t("directory.subtitle")}</p>

        <div className="bg-white border border-stone-200 rounded-2xl p-4 md:p-6 mb-8 grid md:grid-cols-[1fr_240px] gap-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dir-search"
              placeholder={t("directory.searchPlaceholder")}
              className="w-full pl-11 pr-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
          </div>
          <select value={specialty} onChange={(e) => setSpecialty(e.target.value)} data-testid="dir-specialty"
            className="px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
            <option value="">{t("directory.allSpecialties")}</option>
            {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading ? (
          <p className="text-stone-500 text-sm">{t("common.loading")}</p>
        ) : profs.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-14 text-center">
            <Stethoscope className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1.25} />
            <p className="text-stone-500 mb-2">{t("directory.empty")}</p>
            <p className="text-xs text-stone-400 max-w-md mx-auto">{t("directory.emptyHint")}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {profs.map((p) => (
              <div key={p.id} data-testid={`prof-card-${p.slug}`}
                className="group bg-white border border-stone-200 rounded-2xl p-6 hover:border-[#D4A373] hover:shadow-lg transition-all">
                <div className="flex items-center gap-4 mb-4">
                  {p.photo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`} alt={p.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-[#D4A373]/30" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-[#D4A373]/20 flex items-center justify-center">
                      <Stethoscope className="w-6 h-6 text-[#B07D4F]" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-widest text-stone-400">Dr.</p>
                    <h3 className="font-heading text-lg font-medium truncate">{p.name}</h3>
                  </div>
                </div>
                <p className="text-sm text-[#B07D4F] font-medium mb-2 line-clamp-1">{p.specialization}</p>
                {p.clinic_name && (
                  <p className="text-xs text-stone-500 flex items-center gap-1 mb-1 line-clamp-1">
                    <span className="text-stone-400">·</span> {p.clinic_name}
                  </p>
                )}
                {p.clinic_address && (
                  <p className="text-xs text-stone-500 flex items-center gap-1 mb-4 line-clamp-1">
                    <MapPin className="w-3 h-3" /> {p.clinic_address}
                  </p>
                )}
                {p.public_bio && (
                  <p className="text-sm text-stone-600 line-clamp-2 mb-4">{p.public_bio}</p>
                )}
                <button onClick={() => linkAndGo(p)} data-testid={`prof-select-${p.slug}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 text-white px-4 py-2.5 rounded-full hover:bg-stone-700 group-hover:scale-105 transition-all text-sm">
                  {isPatient ? t("directory.linkAndBook") : t("directory.view")} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
